import { createContext, useContext, useMemo } from 'react';

import { useGraphQLQuery } from '@betterment/js-runtime';

import { useForecasterAccountInputs } from './ForecasterAccountInputsContext';

import {
  ForecasterAccountProjectionDocument,
  ForecasterAccountProjectionQuery,
} from '../graphql/forecasterAccountProjection.generated';

const ForecasterPurposeProjectionContext = createContext<{
  account?: ForecasterAccountProjectionQuery['account'];
  isLoading: boolean;
}>({ account: undefined, isLoading: false });

type Props = {
  accountId: string;
  children: React.ReactNode;
};

export const ForecasterAccountProjectionProvider: React.FC<Props> = ({ accountId, children }) => {
  const { formInputs } = useForecasterAccountInputs();

  const { data, loading, previousData } = useGraphQLQuery(ForecasterAccountProjectionDocument, {
    variables: { accountId, input: formInputs },
    throwOnError: true,
  });

  const account = data?.account || previousData?.account;

  const value = useMemo(() => ({ account, isLoading: loading }), [account, loading]);

  return (
    <ForecasterPurposeProjectionContext.Provider value={value}>{children}</ForecasterPurposeProjectionContext.Provider>
  );
};

export const useForecasterAccountProjection = () => {
  const context = useContext(ForecasterPurposeProjectionContext);
  if (!context) {
    throw new Error('useForecasterAccountProjection must be used within a ForecasterAccountProjectionProvider');
  }
  return context;
};
